import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { AlertTriangle, ChevronLeft, ChevronRight, RotateCw, Trash2, X } from 'lucide-react';
import { MondayLogoOutline } from '@vibe/icons';
import {
  KIND,
  PREFILL_STYLE,
  PREFILL_DEFAULT_SIZE,
  SIGNER_FIELD_TYPES,
  requiredMarkKind,
  requiredWordFits,
  signerColor,
  toPixels,
  toNormalised,
  snapSignerSize,
  snapPrefillSize,
  overlayLabel,
  newFieldId,
} from './sign-overlays';
import { DRAG_MIME } from './SignFieldsPalette';
import { DEFAULT_FONT_SIZE } from '../../utils/pdfOverlays';
import '../../utils/pdfjsWorker';

/** Grab size for the bottom-right resize handle, in px. */
const HANDLE = 10;

/**
 * SignMappingCanvas — the document with its placed fields (§4).
 *
 * Sibling of SmartForm's PdfMappingCanvas (same interaction model: drag from
 * the palette OR arm-a-chip-then-click for multi-page placement; pointer
 * gestures on window so a fast drag can't escape) — separate component
 * because SmartSign renders two field kinds with per-type geometry:
 * signatures are pad-sized boxes with an enforced aspect ratio, dates and
 * text are one-line tokens, and resizing snaps to a fixed grid.
 */
export default function SignMappingCanvas({
  fileUrl,
  /** Which document is on screen — stamped onto every field placed here. */
  documentId,
  pageCount,
  fields,
  signers,
  columnTitles,
  prefillColumnIds,
  activeSigner,
  armedChip,
  selectedId,
  onChange,
  onSelect,
  onPlacedFromChip,
  onPageDims,
  /** Rendered page size in CSS px — the palette needs it to size the drag ghost. */
  onRenderedSize,
  /** Clear every placement on THIS document. Omit to hide the control. */
  onClearDocument,
  width = 700,
}) {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ width, height: width * 1.414 });
  const [error, setError] = useState(null);
  const pageRef = useRef(null);
  const dragRef = useRef(null);
  // Which field the pointer is over. Needed because the "required" tag for a box
  // too small to hold the word sits OUTSIDE it, and the box is `overflow-hidden`
  // — so the tag cannot be a child, and a CSS sibling selector cannot reach
  // backwards from a following sibling to the box that is hovered.
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    setPageNumber((p) => Math.min(Math.max(1, p), Math.max(1, pageCount || 1)));
  }, [pageCount]);

  const pageFields = (fields || []).filter((f) => f.page === pageNumber);

  // Prefill placements whose board column was deleted — counted across every
  // page, not just the visible one (a break on page 3 is invisible from page 1).
  const orphans = (fields || []).filter(
    (f) => f.kind === KIND.PREFILL && !prefillColumnIds.has(f.columnId),
  );
  const orphanPages = [...new Set(orphans.map((o) => o.page))].sort((a, b) => a - b);

  const pointToPage = useCallback((clientX, clientY) => {
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const addField = useCallback(
    (payload, atX, atY) => {
      const isSigner = payload.kind === KIND.SIGNER;
      const meta = isSigner ? SIGNER_FIELD_TYPES[payload.type] : null;
      const size = isSigner ? meta.defaultSize : PREFILL_DEFAULT_SIZE;
      const w = size.w * pageSize.width;
      // A square type's default is stated as a width; the height comes from the
      // page so the box is square in PIXELS, not in normalised fractions.
      const h = meta?.square ? w : size.h * pageSize.height;
      // Centre the new box on the cursor, then clamp to the page.
      const norm = toNormalised(
        { left: atX - w / 2, top: atY - h / 2, width: w, height: h },
        pageSize.width,
        pageSize.height,
      );

      const field = isSigner
        ? {
            kind: KIND.SIGNER,
            id: newFieldId(payload.type),
            // Which PDF this box lives on. Without it a field would be drawn
            // onto whichever document happened to occupy its index.
            documentId,
            // The signer ACTIVE AT DROP TIME owns the field (§4) — the palette
            // tab, not anything captured when the drag began.
            signerIndex: activeSigner,
            type: payload.type,
            page: pageNumber,
            ...norm,
            required: payload.type === 'signature',
          }
        : {
            kind: KIND.PREFILL,
            id: newFieldId(payload.columnId),
            documentId,
            columnId: payload.columnId,
            page: pageNumber,
            ...norm,
            fontSize: DEFAULT_FONT_SIZE,
            align: 'left',
            overflow: 'shrink',
          };

      onChange([...(fields || []), field]);
      onSelect(field.id);
    },
    [fields, onChange, onSelect, pageNumber, pageSize, activeSigner, documentId],
  );

  const handleDrop = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    const at = pointToPage(e.clientX, e.clientY);
    if (at && parsed?.kind) addField(parsed, at.x, at.y);
  };

  const handlePageClick = (e) => {
    if (!armedChip) {
      onSelect(null);
      return;
    }
    const at = pointToPage(e.clientX, e.clientY);
    if (!at) return;
    addField(armedChip, at.x, at.y);
    onPlacedFromChip();
  };

  /** Pointer-driven move and resize; resize snaps per kind/type. */
  const beginGesture = (e, field, mode) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(field.id);

    const px = toPixels(field, pageSize.width, pageSize.height);
    dragRef.current = { id: field.id, mode, startX: e.clientX, startY: e.clientY, origin: px };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;

      const next =
        d.mode === 'move'
          ? { ...d.origin, left: d.origin.left + dx, top: d.origin.top + dy }
          : {
              ...d.origin,
              width: Math.max(HANDLE * 2, d.origin.width + dx),
              height: Math.max(HANDLE, d.origin.height + dy),
            };

      const norm = toNormalised(next, pageSize.width, pageSize.height);
      const snapped =
        d.mode === 'resize'
          ? field.kind === KIND.SIGNER
            ? snapSignerSize(field.type, norm.w, norm.h, pageSize.width / pageSize.height)
            : snapPrefillSize(norm.w, norm.h)
          : { w: norm.w, h: norm.h };

      onChange(
        (fields || []).map((f) =>
          f.id === d.id ? { ...f, x: norm.x, y: norm.y, ...snapped } : f,
        ),
      );
    };

    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const removeField = (id) => {
    onChange((fields || []).filter((f) => f.id !== id));
    if (selectedId === id) onSelect(null);
  };

  const armedLabel =
    armedChip &&
    (armedChip.kind === KIND.PREFILL
      ? columnTitles.get(armedChip.columnId) || armedChip.columnId
      : SIGNER_FIELD_TYPES[armedChip.type]?.label || armedChip.type);

  return (
    <div className='flex-1 overflow-y-auto py-6'>
      {armedChip && (
        <p className='mb-2 text-xs text-center' style={{ color: 'var(--color-interactive, #2563EB)' }}>
          Click anywhere on the page to place “{armedLabel}”
        </p>
      )}

      {orphans.length > 0 && (
        <div className='max-w-2xl mx-auto flex items-center gap-2 px-3 py-2 mb-3 text-xs border rounded-lg'
          style={{ borderColor: 'rgba(216,58,82,0.35)', background: 'rgba(216,58,82,0.06)', color: '#d83a52' }}
        >
          <AlertTriangle size={14} className='flex-shrink-0' />
          <span>
            {orphans.length} placement{orphans.length === 1 ? '' : 's'} reference
            {orphans.length === 1 ? 's' : ''} a board column that was deleted
            {orphanPages.length > 0 && ` (page ${orphanPages.join(', ')})`}. Nothing will be filled
            in there — remove them before activating.
          </span>
        </div>
      )}

      {/*
        Only when there is something to clear: a button reading "Clear 0 fields"
        is a control that cannot do anything, and one reading "Clear" would not
        say what it is about to take. The count IS the confirmation.

        Counts fields across EVERY page of this document, not just the visible
        one — clearing is a document-level act, and a box on page 3 is invisible
        from page 1.
      */}
      {onClearDocument && (fields || []).length > 0 && (
        <div className='flex justify-center'>
          <div className='flex justify-end mb-2' style={{ width: pageSize.width }}>
            <button
              type='button'
              onClick={onClearDocument}
              title='Remove every field placed on this document'
              className='flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-red-700 bg-white border border-red-200 hover:bg-red-50'
            >
              <Trash2 size={12} />
              Clear {(fields || []).length} field{(fields || []).length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}

      <div className='flex justify-center'>
        <div
          ref={pageRef}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={handlePageClick}
          className='relative bg-white shadow-lg'
          style={{ cursor: armedChip ? 'crosshair' : 'default', width: pageSize.width }}
        >
          {error ? (
            <div className='px-6 py-16 text-center text-xs text-gray-500'>{error}</div>
          ) : (
            <Document
              file={fileUrl}
              onLoadError={(e) => setError(e?.message || 'The document could not be loaded.')}
              loading={
                <div className='flex items-center justify-center gap-2 px-6 py-16 text-xs text-gray-500'>
                  <RotateCw size={14} className='animate-spin' />
                  Loading document…
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                width={width}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onRenderSuccess={({ width: w, height: h, originalWidth, originalHeight }) => {
                  setPageSize({ width: w, height: h });
                  if (onRenderedSize) onRenderedSize({ width: w, height: h });
                  // Report the page's TRUE size in PDF points (not the render
                  // width) so the props panel can express boxes in the same
                  // unit the stamper draws in. Same contract as SmartForm's
                  // PdfMappingCanvas → onPageDims.
                  if (onPageDims && originalWidth > 0 && originalHeight > 0) {
                    onPageDims(pageNumber, { width: originalWidth, height: originalHeight });
                  }
                }}
              />
            </Document>
          )}

          {/* ── Field layer ── */}
          {pageFields.map((field) => {
            const px = toPixels(field, pageSize.width, pageSize.height);
            const isSigner = field.kind === KIND.SIGNER;
            const orphan = !isSigner && !prefillColumnIds.has(field.columnId);
            const style = isSigner ? signerColor(field.signerIndex) : PREFILL_STYLE;
            const selected = selectedId === field.id;
            const isSignatureBox = isSigner && field.type === 'signature';
            const Icon = isSigner
              ? (SIGNER_FIELD_TYPES[field.type] || SIGNER_FIELD_TYPES.text).icon
              : MondayLogoOutline;
            const label = overlayLabel(field, columnTitles, signers);

            // Prefill fields are filled from the board and cannot be "required"
            // of anyone, so the marker is a signer-field idea only.
            const isRequired = isSigner && field.required !== false;
            const wordInside = isRequired && requiredWordFits(px, isSignatureBox);
            // Everything the word does not fit into gets it on hover or while
            // selected, rendered as a sibling below.
            const wordOutside = isRequired && !wordInside && (hoveredId === field.id || selected);
            const markKind = requiredMarkKind(px);

            return (
              <React.Fragment key={field.id}>
              {wordOutside && (
                <span
                  aria-hidden
                  className='absolute pointer-events-none'
                  style={{
                    left: px.left,
                    // Above the box, unless the box is near the top of the page,
                    // where the tag would be clipped off the paper.
                    ...(px.top >= 14 ? { top: px.top - 13 } : { top: px.top + px.height + 2 }),
                    fontSize: 8,
                    fontWeight: 600,
                    lineHeight: 1.35,
                    color: '#fff',
                    background: '#d83a52',
                    padding: '1px 4px',
                    borderRadius: 3,
                    whiteSpace: 'nowrap',
                    zIndex: 2,
                  }}
                >
                  required
                </span>
              )}
              <div
                onPointerEnter={() => setHoveredId(field.id)}
                onPointerLeave={() => setHoveredId((id) => (id === field.id ? null : id))}
                onPointerDown={(e) => beginGesture(e, field, 'move')}
                // `beginGesture` stops the POINTERDOWN, but the click that
                // follows pointerup still bubbles to the page, whose handler
                // clears the selection — so selecting a field by clicking it
                // deselected on release (the panel flashed and emptied).
                // While a chip is armed the click must still reach the page:
                // that is how a field is placed on top of an existing one.
                onClick={(e) => {
                  if (!armedChip) e.stopPropagation();
                }}
                className={`absolute overflow-hidden group ${
                  isSignatureBox ? 'flex flex-col items-center justify-center' : 'flex items-center gap-1 px-1'
                }`}
                style={{
                  left: px.left,
                  top: px.top,
                  width: px.width,
                  height: px.height,
                  border: orphan ? '1px solid #d83a52' : style.border,
                  background: orphan ? 'rgba(216,58,82,0.10)' : style.background,
                  // INSET, not `outline`: an outline paints outside the border
                  // box, so with an offset a selected field grew ~3px on every
                  // side — a quarter of a date field's height, which reads as
                  // the box resizing rather than being selected.
                  boxShadow: selected ? `inset 0 0 0 2px ${orphan ? '#d83a52' : style.color}` : 'none',
                  cursor: 'move',
                  minHeight: 12,
                  borderRadius: isSignatureBox ? 4 : 0,
                }}
                title={
                  orphan
                    ? `"${field.columnId}" was deleted from the board`
                    : isRequired
                      ? `${label} — required, the signer must complete this`
                      : label
                }
              >
                {/*
                  The required marker, in the only free corner: the remove × is
                  top-right and the resize handle bottom-right. A triangle at
                  6px, or a 4px dot once the box is too small to spare the
                  corner — a checkbox is 13px square and the triangle would eat
                  a quarter of it.

                  Costs no label width, so unlike an asterisk in the text it can
                  never be the first thing `text-overflow: ellipsis` discards.
                */}
                {isRequired &&
                  (markKind === 'corner' ? (
                    <span
                      aria-hidden
                      className='absolute'
                      style={{
                        top: 0,
                        left: 0,
                        width: 0,
                        height: 0,
                        borderTop: '6px solid #d83a52',
                        borderRight: '6px solid transparent',
                      }}
                    />
                  ) : (
                    <span
                      aria-hidden
                      className='absolute'
                      style={{ top: 1, left: 1, width: 4, height: 4, borderRadius: 99, background: '#d83a52' }}
                    />
                  ))}

                {isSignatureBox ? (
                  <>
                    <Icon size={Math.min(14, px.height * 0.4)} style={{ color: style.color }} />
                    <span className='truncate px-1' style={{ fontSize: Math.max(8, Math.min(11, px.height * 0.24)), color: style.color, fontWeight: 500 }}>
                      {label}
                    </span>
                    {wordInside && (
                      <span style={{ fontSize: 8, fontWeight: 600, color: '#d83a52', lineHeight: 1.2 }}>
                        required
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <Icon
                      style={{ width: 9, height: 9, flexShrink: 0, color: orphan ? '#d83a52' : style.color }}
                    />
                    <span
                      className='flex-1 min-w-0 truncate'
                      style={{
                        fontSize: Math.max(7, Math.min(field.fontSize || DEFAULT_FONT_SIZE, px.height)),
                        color: orphan ? '#d83a52' : style.color,
                        textAlign: field.align || 'left',
                      }}
                    >
                      {label}
                    </span>
                    {/*
                      `flex-shrink-0`, so the LABEL truncates to make room rather
                      than this word being ellipsised to "requi…". The threshold
                      in `requiredWordFits` is what stops that trade being a bad
                      one — below it the word is not rendered at all.
                    */}
                    {wordInside && (
                      <span
                        className='flex-shrink-0'
                        style={{ fontSize: 8, fontWeight: 600, color: '#d83a52' }}
                      >
                        required
                      </span>
                    )}
                  </>
                )}

                <button
                  type='button'
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeField(field.id);
                  }}
                  className={`opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${
                    isSignatureBox ? 'absolute top-0.5 right-0.5' : ''
                  }`}
                  aria-label='Remove field'
                  style={{ lineHeight: 0 }}
                >
                  <X style={{ width: 9, height: 9, color: '#d83a52' }} />
                </button>

                <span
                  onPointerDown={(e) => beginGesture(e, field, 'resize')}
                  className='absolute opacity-0 group-hover:opacity-100 transition-opacity'
                  style={{
                    right: -3,
                    bottom: -3,
                    width: HANDLE,
                    height: HANDLE,
                    background: orphan ? '#d83a52' : style.color,
                    borderRadius: 2,
                    cursor: 'nwse-resize',
                  }}
                />
              </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {pageCount > 1 && (
        <div className='flex justify-center mt-4'>
          <div className='flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-full shadow-sm'>
            <button
              type='button'
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              disabled={pageNumber <= 1}
              aria-label='Previous page'
              className='p-1 rounded-full disabled:opacity-30 hover:bg-gray-100'
            >
              <ChevronLeft size={14} />
            </button>
            <span className='px-2 text-xs text-gray-600'>
              Page {pageNumber} of {pageCount}
            </span>
            <button
              type='button'
              onClick={() => setPageNumber((p) => Math.min(pageCount, p + 1))}
              disabled={pageNumber >= pageCount}
              aria-label='Next page'
              className='p-1 rounded-full disabled:opacity-30 hover:bg-gray-100'
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
